import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, fromEvent, merge, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Constant } from '../common/constant';

@Injectable({
  providedIn: 'root'
})
export class InactivityTimeoutService {
  private readonly INACTIVITY_TIMEOUT_MS = 10 * 60 * 1000;
  // The warning modal appears this long before the real cutoff, with a
  // countdown matching this same duration — previously there was no warning
  // at all, just a toast fired right as the session was already gone.
  private readonly WARNING_LEAD_MS = 60 * 1000;
  private timeoutId: any;
  private warningTimeoutId: any;
  private countdownInterval: any;
  private destroyed$ = new Subject<void>();

  // null = no warning showing; otherwise the whole seconds remaining before
  // auto-logout, counting down to 0.
  private secondsRemainingSubject = new BehaviorSubject<number | null>(null);
  secondsRemaining$ = this.secondsRemainingSubject.asObservable();

  constructor(private http: HttpClient, private router: Router, private toaster: ToastrService) {}

  start(): void {
    if (!sessionStorage.getItem('token')) {
      return;
    }
    this.setupActivityListeners();
    this.resetTimer();
  }

  stop(): void {
    this.clearAllTimers();
    this.secondsRemainingSubject.next(null);
    this.destroyed$.next();
    this.destroyed$.complete();
    this.destroyed$ = new Subject<void>();
  }

  resetTimer(): void {
    this.clearAllTimers();
    this.secondsRemainingSubject.next(null);
    this.warningTimeoutId = setTimeout(() => this.beginWarning(), this.INACTIVITY_TIMEOUT_MS - this.WARNING_LEAD_MS);
    this.timeoutId = setTimeout(() => this.handleInactivityTimeout(), this.INACTIVITY_TIMEOUT_MS);
  }

  // Called by the warning modal's "Stay signed in" button — same effect as
  // any tracked activity, just explicit rather than incidental.
  extendSession(): void {
    this.resetTimer();
  }

  logoutNow(): void {
    this.handleInactivityTimeout();
  }

  private clearAllTimers(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    if (this.warningTimeoutId) {
      clearTimeout(this.warningTimeoutId);
      this.warningTimeoutId = null;
    }
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }

  private beginWarning(): void {
    let remaining = Math.round(this.WARNING_LEAD_MS / 1000);
    this.secondsRemainingSubject.next(remaining);
    this.countdownInterval = setInterval(() => {
      remaining--;
      this.secondsRemainingSubject.next(remaining);
      if (remaining <= 0) {
        clearInterval(this.countdownInterval);
        this.countdownInterval = null;
      }
    }, 1000);
  }

  private setupActivityListeners(): void {
    const events = ['mousemove', 'mousedown', 'keypress', 'touchstart', 'scroll', 'wheel'];
    const sources = events.map(eventName => fromEvent(document, eventName));
    if (sources.length > 0) {
      merge(...sources)
        .pipe(takeUntil(this.destroyed$))
        .subscribe(() => this.resetTimer());
    }
  }

  private handleInactivityTimeout(): void {
    this.clearAllTimers();
    this.secondsRemainingSubject.next(null);
    this.toaster.warning('Session expired due to inactivity. Please log in again.');
    const url = Constant.REST_URL + Constant.LOGOUT;
    this.http.post(url, {}).subscribe({
      complete: () => {
        sessionStorage.clear();
        this.router.navigate(['/']);
      },
      error: () => {
        sessionStorage.clear();
        this.router.navigate(['/']);
      },
    });
    this.stop();
  }
}
